import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {useQueryClient} from '@tanstack/react-query';
import {ScorecardsService, type ScorecardWorkstreamCardOut} from 'client';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {t} from 'i18next';
import {$getRoot} from 'lexical';
import {useRef} from 'react';
import {getService} from 'utilities';

import {scorecardDataKeys} from './scorecardQueryKeys';
import styles from './ScorecardWorkstreamUpdateCards.module.css';
import {useScorecardWorkstreamAutosave} from './useScorecardWorkstreamAutosave';

// Decorative stand-in for update text the viewer cannot access.
const RESTRICTED_LINE_WIDTHS = ['92%', '78%', '60%'];

const WorkstreamUpdateCard = ({
  card,
  snapshotDt,
}: {
  card: ScorecardWorkstreamCardOut;
  snapshotDt: string;
}) => {
  const queryClient = useQueryClient();
  const focused = useRef(false);
  const {draft, status, change, flush} = useScorecardWorkstreamAutosave(
    card,
    async (updateId, comment) => {
      const update = await getService(
        ScorecardsService
      ).saveScorecardWorkstreamUpdateApiV1ScorecardsWorkstreamUpdatesWorkstreamIdPut(
        card.workstream_id,
        {
          snapshot_dt: snapshotDt,
          update_id: updateId,
          comment,
        }
      );
      void queryClient.invalidateQueries({queryKey: scorecardDataKeys.all});
      void queryClient.invalidateQueries({queryKey: ['workstream', card.workstream_id]});
      void queryClient.invalidateQueries({queryKey: ['workstreams']});
      return update;
    }
  );
  // Older API responses omit can_view; only an explicit false is restricted.
  const restricted = card.can_view === false;
  const editable = !restricted && Boolean(card.can_edit || card.can_create);
  const placeholder = t(
    snapshotDt === 'current'
      ? 'scorecard_workstream_current_period_placeholder'
      : 'scorecard_workstream_period_empty'
  );

  return (
    <Box
      role="region"
      aria-label={card.name}
      padding="16"
      borderColor="Border Subtle"
      borderWidth="1"
      borderStyle="Solid"
      borderRadius="Medium"
      backgroundColor="Background Base"
      className={styles.card}
    >
      <Box display="flex" flexDirection="column" rowGap="8">
        <Text tagName="h3" size="medium" isBold>
          {card.name}
        </Text>
        {restricted ? (
          <Box
            role="img"
            aria-label={t('scorecard_workstream_restricted')}
            display="flex"
            flexDirection="column"
            rowGap="8"
            paddingY="4"
          >
            {RESTRICTED_LINE_WIDTHS.map((width) => (
              <Box key={width} aria-hidden className={styles.restrictedLine} style={{width}} />
            ))}
          </Box>
        ) : !editable && !draft ? (
          <Text size="small" color="subtle">
            {placeholder}
          </Text>
        ) : (
          <RichTextArea
            source="scorecard-workstream-card"
            value={draft}
            editable={editable}
            placeholder={placeholder}
            hideToolbar
            hideCommentHighlights
            disableAISuggestions
            disableBorder
            textSize="small"
            minHeight={50}
            maxWidth
            editablePaddingX="0"
            editablePaddingY="0"
            placeholderOffsetX={0}
            onChange={(editorState) => {
              // Ignore normalization on mount/refetch; only user edits autosave.
              if (!focused.current || !editable) return;
              const meaningful = editorState.read(
                () => $getRoot().getTextContent().trim().length > 0
              );
              change(editorState.toString(), meaningful);
            }}
            onToggleFocused={(isFocused) => {
              focused.current = isFocused;
              if (!isFocused) void flush();
            }}
          />
        )}
        {status === 'error' && (
          <Box role="status" aria-live="polite">
            <>
              <Text size="small" color="error">
                {t('scorecard_workstream_save_error')}
              </Text>
              <Button size="small" variant="transparent" onClick={() => void flush()}>
                {t('retry')}
              </Button>
            </>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const ScorecardWorkstreamUpdateCards = ({
  cards,
  snapshotDt,
}: {
  cards: ScorecardWorkstreamCardOut[];
  snapshotDt: string;
}) =>
  cards.length > 0 ? (
    <Box marginTop="32" marginBottom="24" className={styles.cards}>
      {cards.map((card) => (
        <WorkstreamUpdateCard
          key={`${snapshotDt}:${card.workstream_id}`}
          card={card}
          snapshotDt={snapshotDt}
        />
      ))}
    </Box>
  ) : null;
